import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { EntityType, MapEntity } from "@mapmycustomers/shared/types/entity";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import { parseAreaType } from "@mapmycustomers/shared/util/territory/areaTypes";
import { LoadingSpinner } from "@mapmycustomers/ui";

import DataViewButton from "@app/component/DataView/DataViewButton";
import type { TextGetters } from "@app/component/RecordList";
import {
  monoEntitiesCountTextGetterFactory,
  monoEntitiesPaginationTextGetterFactory,
  NoData,
} from "@app/component/RecordList";
import useMapView from "@app/component/view/utils/useMapView";
import TerritoryFeature from "@app/scene/map/components/features/TerritoryFeature";
import EntityItem from "@app/scene/map/components/MapMode/components/Sidebar/components/EntityItem";
import EntityRecordView from "@app/scene/map/components/MapMode/components/Sidebar/components/EntityRecordView";
import SelectedPinsSidebar from "@app/scene/map/components/SelectedPinsSidebar";
import ExitLassoModeButton from "@app/scene/map/components/sidebar/ExitLassoModeButton";
import MultiPinBackButton from "@app/scene/map/components/sidebar/MultiPinBackButton";
import MultiPinRecordList from "@app/scene/map/components/sidebar/MultiPinRecordList";
import { RecordList } from "@app/scene/map/components/sidebar/RecordList";
import BaseSidebar from "@app/scene/map/components/sidebar/Sidebar";
import Title from "@app/scene/map/components/TerritoriesMode/components/Title";
import useBoundsContext from "@app/scene/map/components/TerritoriesMode/utils/useBoundsContext";
import {
  exitLassoMode,
  exitTerritoryMode,
  fetchTerritoryRecords,
  hideMultiPin,
  setDataViewData,
  setTerritoryMode,
  toggleDataViewVisibility,
} from "@app/scene/map/store/actions";
import { getMapViewTool, getMultiPin } from "@app/scene/map/store/mapMode/selectors";
import {
  areTerritoryRecordsLoading,
  getTerritory,
  getTerritoryMode,
  getTerritoryRecords,
  getTerritoryRecordsTotalCount,
  isTerritoryDataViewLoading,
} from "@app/scene/map/store/territoryMode/selectors";
import { isEntityViewVisible } from "@app/store/entityView";
import { hideEntityView } from "@app/store/entityView/actions";
import { addUserRecent } from "@app/store/globalSearch/actions";
import { RootState } from "@app/store/rootReducer";
import { mapEntityIdGetter } from "@app/util/map/idGetters";

import EditDrawer from "./component/EditDrawer";
import TerritoryProps from "./component/TerritoryProps";
import TerritoryRecord from "./component/TerritoryRecord";
import styles from "./Sidebar.module.scss";

const messages = defineMessages({
  backToLocation: {
    id: "map.territoryMode.sidebar.multiPin.backToLocation",
    defaultMessage: "Back to Location",
    description: "Territory records list for multi-pin - Back to Location",
  },
  backToTerritory: {
    id: "map.territoryMode.sidebar.multiPin.backToTerritory",
    defaultMessage: "Back to Territory",
    description: "Territory selected records list - Back to Territory",
  },
  noData: {
    id: "map.territoriesMode.sidebar.noData.text",
    defaultMessage:
      "There are no Records in this territory yet. Create a Record within the selected bounds.",
    description: "No Result text in Territory Mode",
  },
  placeholder: {
    id: "map.territoriesMode.sidebar.search.territoryPlaceholder",
    defaultMessage: "Search records in this territory",
    description: "Territory records list - search placeholder",
  },
});

type EditSidebarProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const EditSidebar: React.FC<EditSidebarProps> = ({
  dataViewLoading,
  isEntityViewVisible,
  loading,
  mode,
  multiPin,
  onAddUserRecent,
  onChangeDataViewData,
  onChangeMode,
  onExitLassoMode,
  onExitTerritoryMode,
  onFetchRecords,
  onHideEntityView,
  onHideMultiPin,
  onToggleDataViewVisibility,
  records,
  recordsCount,
  selectedTool,
  territory,
}) => {
  const intl = useIntl();

  const {
    viewState: { filter, range },
  } = useMapView();

  useEffect(() => {
    if (territory) {
      let data;
      if (territory.territoryDetail.type === "points") {
        data = territory.territoryDetail.points.coordinates;
      } else {
        const areaType = territory.territoryDetail.type;
        if (areaType) {
          const parsedAreaType = parseAreaType(areaType as AreaType);
          data = parsedAreaType
            ? { areaType, regionIds: territory.territoryDetail[parsedAreaType.level] }
            : undefined;
        }
      }
      if (data) {
        onChangeDataViewData(data);
      }
    }
  }, [onChangeDataViewData, territory]);

  const isLassoMode = selectedTool === MapTool.LASSO;
  const isMultiPinMode = !!multiPin;

  useEffect(() => {
    // initialize
    if (territory?.id) {
      onChangeMode("view");
      onFetchRecords({});
      onAddUserRecent({ entity: { id: territory.id, type: EntityType.TERRITORY } });
    }
  }, [onAddUserRecent, onFetchRecords, onChangeMode, territory?.id]);

  const handleRangeChange = useCallback((range) => onFetchRecords({ range }), [onFetchRecords]);
  const handleSearch = useCallback((search) => onFetchRecords({ search }), [onFetchRecords]);

  const handleClose = useCallback(() => {
    if (isMultiPinMode) {
      onHideMultiPin();
    } else if (isLassoMode) {
      onExitLassoMode();
    } else {
      onExitTerritoryMode?.();
    }
  }, [onExitLassoMode, isLassoMode, isMultiPinMode, onHideMultiPin, onExitTerritoryMode]);

  const textGetters: TextGetters = useMemo(
    () => ({
      countTextGetter: monoEntitiesCountTextGetterFactory(intl),
      paginationTextGetter: monoEntitiesPaginationTextGetterFactory(intl),
    }),
    [intl]
  );

  const { setColor: setBannerColor } = useBoundsContext();
  useEffect(
    () => {
      setBannerColor(territory?.color || undefined);
    },
    // we need "mode" in deps to reset color to territory own color when
    // user was in edit modal, changed color there and then cancelled it
    [mode, setBannerColor, territory?.color]
  );

  const handleHideEditDrawer = useCallback(() => {
    onChangeMode("view");
  }, [onChangeMode]);

  const handleDataViewClick = useCallback(
    () => onToggleDataViewVisibility(),
    [onToggleDataViewVisibility]
  );

  const handleHideEntityView = useCallback(() => onHideEntityView(), [onHideEntityView]);

  if (!territory?.id) {
    return null;
  }

  return mode === "edit" ? (
    <EditDrawer onClose={handleHideEditDrawer} territory={territory!} />
  ) : isLassoMode ? (
    <SelectedPinsSidebar
      beforeTitle={<ExitLassoModeButton label={intl.formatMessage(messages.backToTerritory)} />}
      itemComponent={TerritoryRecord}
      loading={loading}
      onDataViewClick={handleDataViewClick}
      onFetchRecords={onFetchRecords}
      onRangeChange={handleRangeChange}
      onSearch={handleSearch}
      range={range}
      records={records}
      recordsCount={recordsCount}
      showDataView
    />
  ) : (
    <BaseSidebar
      beforeTitle={<MultiPinBackButton />}
      className={styles.container}
      onClose={handleClose}
      titleRaw={<Title />}
      visible
      wrapper={isEntityViewVisible}
    >
      <EntityRecordView
        backButtonLabel={isMultiPinMode ? intl.formatMessage(messages.backToLocation) : undefined}
        onClose={handleHideEntityView}
      />

      {!isEntityViewVisible && (
        <>
          {territory ? (
            <>
              {isMultiPinMode ? (
                <MultiPinRecordList itemComponent={EntityItem} />
              ) : (
                <>
                  <TerritoryFeature
                    disabled={isLassoMode}
                    selectedTerritoryId={territory.id}
                    territories={[territory]}
                    type="selectedTerritory"
                  />
                  <RecordList<MapEntity>
                    actions={territory && <TerritoryProps territory={territory} />}
                    headerClassName={styles.recordListHeader}
                    idGetter={mapEntityIdGetter}
                    itemComponent={TerritoryRecord}
                    loading={loading}
                    noData={
                      <NoData
                        showAction={Object.keys(filter).length > 0}
                        text={intl.formatMessage(messages.noData)}
                      />
                    }
                    onRangeChange={handleRangeChange}
                    onSearch={handleSearch}
                    range={range}
                    recordCountClassName={styles.recordCountClassName}
                    records={records}
                    searchActions={
                      <DataViewButton
                        disableTooltip
                        entityCount={recordsCount}
                        loading={dataViewLoading}
                        onClick={handleDataViewClick}
                      />
                    }
                    searchPlaceholder={intl.formatMessage(messages.placeholder)}
                    textGetters={textGetters}
                    total={recordsCount}
                  />
                </>
              )}
            </>
          ) : (
            <LoadingSpinner />
          )}
        </>
      )}
    </BaseSidebar>
  );
};

const mapStateToProps = (state: RootState) => ({
  dataViewLoading: isTerritoryDataViewLoading(state),
  isEntityViewVisible: isEntityViewVisible(state),
  loading: areTerritoryRecordsLoading(state),
  mode: getTerritoryMode(state),
  multiPin: getMultiPin(state),
  records: getTerritoryRecords(state),
  recordsCount: getTerritoryRecordsTotalCount(state),
  selectedTool: getMapViewTool(state),
  territory: getTerritory(state),
});

const mapDispatchToProps = {
  onAddUserRecent: addUserRecent,
  onChangeDataViewData: setDataViewData,
  onChangeMode: setTerritoryMode,
  onExitLassoMode: exitLassoMode,
  onExitTerritoryMode: exitTerritoryMode.request,
  onFetchRecords: fetchTerritoryRecords.request,
  onHideEntityView: hideEntityView,
  onHideMultiPin: hideMultiPin,
  onToggleDataViewVisibility: toggleDataViewVisibility,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditSidebar);
